// =============================================================================
// VulnTracker — Settings Page
// =============================================================================
import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useHealthStatus, useAppInfo, useSyncSchedule, useUpdateSyncSchedule } from '@/api/hooks';

export function SettingsPage() {
  const { user } = useAuthStore();
  const { data: health } = useHealthStatus();
  const { data: info } = useAppInfo();
  const { data: syncSchedule } = useSyncSchedule();
  const updateSyncSchedule = useUpdateSyncSchedule();

  const [syncTime, setSyncTime] = useState('02:00');
  const [syncSaved, setSyncSaved] = useState(false);

  // Sync server value into local state once loaded
  useEffect(() => {
    if (syncSchedule?.cve_sync_time) {
      setSyncTime(syncSchedule.cve_sync_time);
    }
  }, [syncSchedule?.cve_sync_time]);

  const isAdmin = user?.role === 'admin';

  // Live uptime counter: seeds from server value, ticks up every second locally
  const uptimeRef = useRef<number | null>(null);
  const [displayedUptime, setDisplayedUptime] = useState<number | null>(null);

  // Sync to server value whenever the query refetches
  useEffect(() => {
    if (info?.uptime_seconds != null) {
      uptimeRef.current = Math.round(info.uptime_seconds);
      setDisplayedUptime(uptimeRef.current);
    }
  }, [info?.uptime_seconds]);

  // Tick every second between server refetches
  useEffect(() => {
    const id = setInterval(() => {
      if (uptimeRef.current !== null) {
        uptimeRef.current += 1;
        setDisplayedUptime(uptimeRef.current);
      }
    }, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Platform configuration and system status
        </p>
      </div>

      {/* User profile */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">My Profile</h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">Name</dt>
            <dd className="text-sm text-gray-900">{user?.full_name || '—'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Email</dt>
            <dd className="text-sm text-gray-900">{user?.email || '—'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Role</dt>
            <dd className="text-sm text-gray-900">
              <span className="badge bg-primary-100 text-primary-800">
                {user?.role?.replace('_', ' ') || '—'}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Status</dt>
            <dd className="text-sm text-gray-900">
              {user?.is_active ? (
                <span className="text-green-600">Active</span>
              ) : (
                <span className="text-red-600">Inactive</span>
              )}
            </dd>
          </div>
        </dl>
      </div>

      {/* About / Platform info */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">About</h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">Platform Status</dt>
            <dd className="mt-1 flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${health?.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-sm font-medium text-gray-900">
                {health?.status === 'healthy' ? 'Online' : health ? 'Unavailable' : 'Checking…'}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Version</dt>
            <dd className="text-sm text-gray-900">{info?.version ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Environment</dt>
            <dd className="text-sm text-gray-900 capitalize">{info?.environment ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500 flex items-center gap-1">
              Server Uptime
              <span
                title="How long the server has been running continuously since its last restart. A longer uptime means the server has been stable and uninterrupted."
                className="inline-flex items-center justify-center h-3.5 w-3.5 rounded-full bg-gray-300 text-gray-600 text-[10px] font-bold cursor-help leading-none select-none"
              >i</span>
            </dt>
            <dd className="text-sm text-gray-900">
              {displayedUptime !== null ? formatUptime(displayedUptime) : '—'}
            </dd>
          </div>
        </dl>
      </div>

      {/* CVE Sync Schedule — admin only */}
      {isAdmin && (
        <div className="card">
          <h3 className="text-lg font-semibold mb-1">CVE Database Sync Schedule</h3>
          <p className="text-sm text-gray-500 mb-4">
            Set the daily time (UTC) at which VulnTracker synchronises with CVE database.
          </p>
          <div className="flex items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sync time (UTC)
              </label>
              <input
                type="time"
                className="input-field px-3 py-2 w-36"
                value={syncTime}
                onChange={(e) => { setSyncTime(e.target.value); setSyncSaved(false); }}
              />
            </div>
            <button
              className="btn-primary"
              disabled={updateSyncSchedule.isPending}
              onClick={async () => {
                await updateSyncSchedule.mutateAsync(syncTime);
                setSyncSaved(true);
              }}
            >
              {updateSyncSchedule.isPending ? 'Saving…' : 'Save'}
            </button>
            {syncSaved && (
              <span className="text-sm text-green-600 font-medium">Saved ✓</span>
            )}
          </div>
          <p className="mt-3 text-xs text-gray-400">
            Current schedule: every day at <span className="font-semibold">{syncSchedule?.cve_sync_time ?? '—'} UTC</span>
          </p>
        </div>
      )}

      {/* API keys & notification preferences — placeholder */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">API Keys</h3>
        <p className="text-sm text-gray-500">
          API key management will be available here. Generate and revoke API keys for
          programmatic access to VulnTracker.
        </p>
        <button className="btn-primary mt-4" disabled>
          Generate New API Key
        </button>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Notification Preferences</h3>
        <p className="text-sm text-gray-500">
          Configure your notification channels, digest schedules, and quiet hours.
        </p>
        <div className="mt-4 space-y-3">
          {['email', 'slack', 'teams', 'jira'].map((ch) => (
            <div key={ch} className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm font-medium capitalize">{ch}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" defaultChecked={ch === 'email'} className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function formatUptime(seconds: number): string {
  const s = Math.floor(seconds);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m`;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}
